// Tenant display timezone for the POS surfaces.
//
// Mirrors admin-mobile-app/src/utils/tenantTime.js. Keep the two byte-identical apart
// from the one header line naming the mirror direction, following the existing
// money.js / partyLabel.js / addableOrder.js duplication convention.
//
// WHY THIS FILE EXISTS. "Is this order from today?" and "what time was it placed?"
// were both answered in hardcoded `America/New_York` in two 25,000-line files. For a
// tenant in IST (+10:30 from Eastern) that is not a cosmetic defect: an order taken at
// 08:00 local is 21:30 the PREVIOUS Eastern day, so the queue's "today" filter drops
// it and the ticket that prints for the kitchen is ten and a half hours wrong. Roughly
// every order before ~10:30 local vanishes.
//
// ONE PLACE, so the queue predicate, the guest bill and the kitchen chit cannot
// disagree about which day an order belongs to. A printed ticket has to read the same
// on web and on mobile, which is why this is a shared pure resolver tested in both
// packages rather than an inline fix at each call site.
//
// THE DEFAULT IS EASTERN, DELIBERATELY, AND IT IS THE WHOLE NON-REGRESSION CONTRACT.
// Absent, blank, non-string and unrecognised configuration all resolve to
// `America/New_York`, so a tenant that has not set a timezone behaves exactly as it did
// before this module existed.
//
// AND NEVER TO DEVICE-LOCAL. `Intl` would happily default to the tablet's own zone,
// which is the tempting fallback and the wrong one twice over: it would silently change
// behaviour for every existing install on upgrade, and it would make the answer depend
// on where the staff member's device happens to be rather than on where the restaurant
// is. A cashier's tablet carried across a timezone must not change which orders are
// "today".
//
// Pure: no React, no native imports. The module-level tenant zone follows the
// `setStoreCurrency` / `getStoreCurrency` precedent in money.js -- one tenant setting,
// set once when it arrives, then read by every render and every print builder without
// threading a parameter through a monolith.

export const DEFAULT_TIMEZONE = 'America/New_York';

let _tenantTimezone = DEFAULT_TIMEZONE;

/**
 * isValidTimezone(tz) -> boolean
 *
 * Guarded `Intl.DateTimeFormat` construction. An unrecognised IANA name throws a
 * RangeError, and the throw must never reach the queue -- a typo in a settings field
 * is not a reason for a cashier to see a blank order list.
 *
 * Returns false when `Intl` itself is unavailable, which pushes the caller onto
 * DEFAULT_TIMEZONE. That is the same string the old hardcoded call sites passed, so a
 * runtime without full Intl behaves precisely as it did before.
 */
export function isValidTimezone(tz) {
  const s = typeof tz === 'string' ? tz.trim() : '';
  if (!s) return false;
  try {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return false;
    new Intl.DateTimeFormat('en-US', { timeZone: s });
    return true;
  } catch {
    return false;
  }
}

/**
 * normalizeTimezone(tz) -> valid IANA zone string
 *
 * Total function: always returns something safe to hand to `toLocaleString`.
 */
export function normalizeTimezone(tz) {
  const s = typeof tz === 'string' ? tz.trim() : '';
  return isValidTimezone(s) ? s : DEFAULT_TIMEZONE;
}

/**
 * The places a tenant timezone can legitimately live, in precedence order.
 *
 * A dedicated `timezone` wins; otherwise the two blocks that ALREADY carry one
 * (`orderingClosedHours.timezone`, `serviceWindows.timezone`) are consulted, because
 * that plumbing predates this module and a tenant who set their zone there has already
 * told us where they are. Inventing a third field and ignoring those two would mean an
 * India tenant configuring "Asia/Kolkata" for ordering hours still gets Eastern chits.
 *
 * `settings.*` is checked alongside the bare shape because `GET /admin/general` answers
 * `{ settings: {...} }` while the POS queue response carries the resolved value flat.
 */
function timezoneCandidates(source) {
  if (typeof source === 'string') return [source];
  if (!source || typeof source !== 'object') return [];
  const nested = source.settings && typeof source.settings === 'object' ? source.settings : {};
  return [
    source.timezone,
    source.timeZone,
    nested.timezone,
    nested.timeZone,
    source.orderingClosedHours && source.orderingClosedHours.timezone,
    nested.orderingClosedHours && nested.orderingClosedHours.timezone,
    source.serviceWindows && source.serviceWindows.timezone,
    nested.serviceWindows && nested.serviceWindows.timezone,
  ];
}

/**
 * readTenantTimezone(source) -> valid zone string, or '' when the source carries none
 *
 * The distinction from `resolveTenantTimezone` matters for `setTenantTimezone`: a
 * payload that says nothing about the timezone must not overwrite a value another
 * payload already established. Collapsing "absent" into "Eastern" here would let a
 * general-settings 403 (the POS-only cashier case) stomp the zone the queue response
 * just supplied.
 */
export function readTenantTimezone(source) {
  const candidates = timezoneCandidates(source);
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (isValidTimezone(trimmed)) return trimmed;
  }
  return '';
}

/**
 * resolveTenantTimezone(settings) -> valid zone string
 *
 * The pure resolver. Absent, empty, null, non-string and unrecognised-IANA all land on
 * `America/New_York`.
 */
export function resolveTenantTimezone(settings) {
  return readTenantTimezone(settings) || DEFAULT_TIMEZONE;
}

/**
 * setTenantTimezone(source) -> the zone now in effect
 *
 * Only a source that actually CARRIES a usable timezone may set it; anything else
 * leaves the current value alone. Two callers feed this -- the POS queue response and
 * `/admin/general` -- and they derive from the same stored settings item, so they cannot
 * disagree. What they DO differ in is availability: a POS-only cashier's
 * `/admin/general` call 403s, so the queue response is the only source they have.
 */
export function setTenantTimezone(source) {
  const found = readTenantTimezone(source);
  if (found) _tenantTimezone = found;
  return _tenantTimezone;
}

export function getTenantTimezone() {
  return _tenantTimezone;
}

/**
 * resetTenantTimezone() -> DEFAULT_TIMEZONE
 *
 * Back to Eastern. For sign-out and for tests, so one test's tenant cannot leak into
 * the next.
 */
export function resetTenantTimezone() {
  _tenantTimezone = DEFAULT_TIMEZONE;
  return _tenantTimezone;
}

/**
 * isTodayInZone(orderTimestampSeconds, timeZone) -> boolean
 *
 * The timezone-parameterised replacement for `isTodayEastern`. Guards are preserved
 * exactly: a missing timestamp, an unparseable one and a thrown formatter all answer
 * false, because a "today" filter that fails open would show a cashier yesterday's
 * orders as live tickets.
 *
 * epoch SECONDS when numeric (the unit used across the app); anything else is handed to
 * `new Date` as-is.
 */
export function isTodayInZone(orderTimestampSeconds, timeZone) {
  if (!orderTimestampSeconds && orderTimestampSeconds !== 0) return false;
  try {
    const zone = normalizeTimezone(timeZone === undefined ? _tenantTimezone : timeZone);
    const n = Number(orderTimestampSeconds);
    const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(orderTimestampSeconds);
    if (Number.isNaN(dt.getTime())) return false;
    const today = new Date();

    const a = dt.toLocaleDateString('en-US', { timeZone: zone });
    const b = today.toLocaleDateString('en-US', { timeZone: zone });
    return a === b;
  } catch {
    return false;
  }
}

/**
 * formatDateTimeInZone(value, timeZone) -> display string or 'N/A'
 *
 * The parameterised replacement for `formatDateTimeEastern`. Output is byte-identical to
 * that function when the zone is `America/New_York` -- same locale, same options, same
 * guards -- which is what lets every receipt and report call site switch over with no
 * visible change for existing tenants.
 */
export function formatDateTimeInZone(value, timeZone) {
  if (!value && value !== 0) return 'N/A';
  try {
    const zone = normalizeTimezone(timeZone === undefined ? _tenantTimezone : timeZone);
    const n = Number(value);
    const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(value);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleString('en-US', { timeZone: zone });
  } catch {
    return 'N/A';
  }
}

/**
 * dateKeyInZone(value, timeZone) -> 'YYYY-MM-DD' or ''
 *
 * The `en-CA` sortable day key the backlog/queue reads send as `start`/`end`. Same
 * question as `isTodayInZone` asked of the server instead of the client, so it has to
 * use the same zone -- an IST tenant querying an Eastern day key would filter out
 * server-side exactly the orders the client just stopped filtering out.
 *
 * `value` defaults to now, which is every current caller.
 */
export function dateKeyInZone(value, timeZone) {
  try {
    const zone = normalizeTimezone(timeZone === undefined ? _tenantTimezone : timeZone);
    let dt;
    if (value === undefined || value === null || value === '') {
      dt = new Date();
    } else {
      const n = Number(value);
      dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(value);
    }
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString('en-CA', { timeZone: zone });
  } catch {
    return '';
  }
}
